import { useEffect, useState } from 'react'
import { PlaySquare, Plus, Pencil, Trash2, CheckCircle, AlertCircle, X, Image } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  listAds,
  createAd,
  updateAd,
  deleteAd,
  listSponsors,
  type AdvertiserAdItem,
  type AdvertiserSponsorItem,
} from '@/lib/api/advertiser'

const AD_TYPES = [
  { value: 'preroll_video', label: 'Pre-roll video' },
  { value: 'display_image', label: 'Display image' },
] as const

const AD_STATUSES = ['active', 'paused', 'draft'] as const

export function AdvertiserAds() {
  const [ads, setAds] = useState<AdvertiserAdItem[]>([])
  const [sponsors, setSponsors] = useState<AdvertiserSponsorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AdvertiserAdItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{
    name: string
    type: 'preroll_video' | 'display_image'
    sponsorId: string
    videoUrl: string
    imageUrl: string
    clickUrl: string
    skipAfterSeconds: string
    status: string
  }>({
    name: '',
    type: 'preroll_video',
    sponsorId: '',
    videoUrl: '',
    imageUrl: '',
    clickUrl: '',
    skipAfterSeconds: '5',
    status: 'draft',
  })

  async function reload() {
    try {
      setLoading(true)
      const [adsRes, sponsorsRes] = await Promise.all([
        listAds({ limit: 200 }),
        listSponsors({ limit: 200 }),
      ])
      setAds(adsRes.items)
      setSponsors(sponsorsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ads')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  function openCreate() {
    setEditing(null)
    setForm({
      name: '',
      type: 'preroll_video',
      sponsorId: '',
      videoUrl: '',
      imageUrl: '',
      clickUrl: '',
      skipAfterSeconds: '5',
      status: 'draft',
    })
    setModalOpen(true)
  }

  function openEdit(ad: AdvertiserAdItem) {
    setEditing(ad)
    setForm({
      name: ad.name ?? '',
      type: ad.type,
      sponsorId: ad.sponsorId ?? '',
      videoUrl: ad.videoUrl ?? '',
      imageUrl: ad.imageUrl ?? '',
      clickUrl: ad.clickUrl ?? '',
      skipAfterSeconds: String(ad.skipAfterSeconds ?? 5),
      status: ad.status ?? 'draft',
    })
    setModalOpen(true)
  }

  async function handleSave() {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const payload = {
        name: form.name,
        type: form.type,
        sponsorId: form.sponsorId || null,
        videoUrl: form.videoUrl || null,
        imageUrl: form.imageUrl || null,
        clickUrl: form.clickUrl || undefined,
        skipAfterSeconds: form.type === 'preroll_video' ? Number(form.skipAfterSeconds) : undefined,
        status: form.status,
      }
      if (editing) {
        await updateAd(editing.adId, payload)
        setNotice(`Ad "${form.name}" updated.`)
      } else {
        await createAd(payload)
        setNotice(`Ad "${form.name}" created.`)
      }
      setModalOpen(false)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(ad: AdvertiserAdItem) {
    if (!window.confirm(`Delete ad "${ad.name ?? ad.adId}"? This cannot be undone.`)) return
    try {
      await deleteAd(ad.adId)
      setNotice(`Ad deleted.`)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  function sponsorName(id?: string | null) {
    if (!id) return null
    return sponsors.find((s) => s.sponsorId === id)?.name ?? id
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ad creatives</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage pre-roll video and display image ads.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#D72638] hover:bg-[#D72638]/90 text-white w-fit"
        >
          <Plus className="w-4 h-4 mr-2" />
          New ad
        </Button>
      </div>

      {/* Notices */}
      {notice && (
        <div className="rounded-lg border border-green-700 bg-green-900/20 px-4 py-2 flex items-center gap-2 text-sm text-green-300">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-2 flex items-center gap-2 text-sm text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse">Loading ads…</p>
      ) : ads.length === 0 ? (
        <EmptyState onAdd={openCreate} />
      ) : (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Ad
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden lg:table-cell">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {ads.map((ad) => (
                <tr
                  key={ad.adId}
                  className="border-b border-gray-900 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center shrink-0">
                        {ad.type === 'preroll_video' ? (
                          <PlaySquare className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Image className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{ad.name ?? ad.adId}</p>
                        {ad.clickUrl && (
                          <p className="text-xs text-gray-500 truncate max-w-[180px]">
                            {ad.clickUrl}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <TypeBadge type={ad.type} />
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-xs text-gray-300">
                      {sponsorName(ad.sponsorId) ?? <span className="text-gray-600">—</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <StatusBadge status={ad.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(ad)}
                        className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(ad)}
                        className="p-1.5 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6 space-y-5 shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing ? 'Edit ad creative' : 'New ad creative'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Name *</span>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Safaricom Q1 Pre-roll"
                  className="bg-gray-900 border-gray-700"
                />
              </label>

              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Ad type</span>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value as typeof form.type }))
                  }
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  {AD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Sponsor</span>
                <select
                  value={form.sponsorId}
                  onChange={(e) => setForm((f) => ({ ...f, sponsorId: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  <option value="">— No sponsor —</option>
                  {sponsors.map((s) => (
                    <option key={s.sponsorId} value={s.sponsorId}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>

              {form.type === 'preroll_video' && (
                <>
                  <label className="block">
                    <span className="text-xs text-gray-400 mb-1 block">Video URL</span>
                    <Input
                      value={form.videoUrl}
                      onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
                      placeholder="https://cdn.example.com/ad.mp4"
                      className="bg-gray-900 border-gray-700"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-400 mb-1 block">
                      Skip after (seconds)
                    </span>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={form.skipAfterSeconds}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, skipAfterSeconds: e.target.value }))
                      }
                      className="bg-gray-900 border-gray-700"
                    />
                  </label>
                </>
              )}

              {form.type === 'display_image' && (
                <label className="block">
                  <span className="text-xs text-gray-400 mb-1 block">Image URL</span>
                  <Input
                    value={form.imageUrl}
                    onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                    placeholder="https://cdn.example.com/banner.jpg"
                    className="bg-gray-900 border-gray-700"
                  />
                </label>
              )}

              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Click URL</span>
                <Input
                  value={form.clickUrl}
                  onChange={(e) => setForm((f) => ({ ...f, clickUrl: e.target.value }))}
                  placeholder="https://brand.com/landing"
                  className="bg-gray-900 border-gray-700"
                />
              </label>

              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Status</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  {AD_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 bg-[#D72638] hover:bg-[#D72638]/90 text-white"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create ad'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setModalOpen(false)}
                className="border-gray-700 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TypeBadge({ type }: { type: string }) {
  return type === 'preroll_video' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-400">
      <PlaySquare className="w-3 h-3" /> Pre-roll
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-2 py-0.5 text-[11px] font-medium text-orange-400">
      <Image className="w-3 h-3" /> Display
    </span>
  )
}

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    active: 'bg-green-500/10 text-green-400',
    paused: 'bg-yellow-500/10 text-yellow-400',
    draft: 'bg-gray-700/30 text-gray-400',
  }
  const cls = map[status ?? 'draft'] ?? 'bg-gray-700/30 text-gray-400'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
      {status ?? 'draft'}
    </span>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-700 p-10 text-center space-y-3">
      <PlaySquare className="w-8 h-8 text-gray-600 mx-auto" />
      <p className="text-sm text-gray-400">No ad creatives yet.</p>
      <Button
        onClick={onAdd}
        variant="secondary"
        className="border-gray-700 text-gray-300 hover:bg-white/5"
      >
        <Plus className="w-4 h-4 mr-2" />
        Create your first ad
      </Button>
    </div>
  )
}
